import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UpgradeModal } from "./components/UpgradeModal";
import { SheetMusicViewer } from "./components/SheetMusicViewer";
import { SheetViewModal } from "./components/SheetViewModal";
import { transcribeWithProgress, checkBackendHealth } from "./api/transcribe";
import { FUWALO_LOGO_PATH } from "./constants/branding";
import { useMidiPlayer } from "./hooks/useMidiPlayer";
import { applySeo } from "./lib/seo";

type TranscriptionStatus = "idle" | "ready" | "uploading" | "transcribing" | "success" | "error";

type HistoryItem = {
  id: string;
  createdAt: string;
  title: string;
  summary: string;
};

type ApiResult = {
  title: string;
  summary: string;
  midiUrl?: string;
  audioUrl?: string;
  plotUrl?: string;
  musicXmlUrl?: string | null;
};

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

type AudioPreviewBarProps = {
  originalSrc: string | null;
  midiSrc: string | null;
  viewMode: "original" | "midi";
  onViewModeChange: (mode: "original" | "midi") => void;
  duration: string;
  hasMidi: boolean;
};

const AudioPreviewBar: React.FC<AudioPreviewBarProps> = ({
  originalSrc,
  midiSrc,
  viewMode,
  onViewModeChange,
  duration,
  hasMidi,
}) => {
  const originalRef = useRef<HTMLAudioElement>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const midiPlayer = useMidiPlayer(viewMode === "midi" ? midiSrc : null);

  const isPlaying = viewMode === "original" ? audioPlaying : midiPlayer.isPlaying;
  const displayCurrentTime =
    viewMode === "original"
      ? formatDuration(audioCurrentTime)
      : formatDuration(midiPlayer.currentTime);
  const displayDuration =
    viewMode === "original"
      ? duration
      : midiPlayer.duration > 0
        ? formatDuration(midiPlayer.duration)
        : duration;
  const audioDurationSec = parseDuration(duration) || 1;
  const progressPercent =
    viewMode === "original"
      ? audioDurationSec > 0
        ? Math.min(100, (audioCurrentTime / audioDurationSec) * 100)
        : 0
      : midiPlayer.duration > 0
        ? Math.min(100, (midiPlayer.currentTime / midiPlayer.duration) * 100)
        : 0;

  const handlePlayPause = useCallback(() => {
    if (viewMode === "original") {
      const audio = originalRef.current;
      if (!audio || !originalSrc) return;
      if (audioPlaying) {
        audio.pause();
        setAudioPlaying(false);
      } else {
        audio.play().then(() => setAudioPlaying(true));
      }
    } else {
      if (!midiSrc) return;
      if (midiPlayer.isPlaying) {
        midiPlayer.pause();
      } else {
        midiPlayer.play();
      }
    }
  }, [viewMode, originalSrc, midiSrc, audioPlaying, midiPlayer]);

  const handleToggle = useCallback(() => {
    if (!hasMidi) return;
    const wasPlaying = isPlaying;
    if (viewMode === "original") {
      originalRef.current?.pause();
      setAudioPlaying(false);
    } else {
      midiPlayer.stop();
    }
    onViewModeChange(viewMode === "original" ? "midi" : "original");
    if (wasPlaying) {
      setTimeout(() => handlePlayPause(), 50);
    }
  }, [hasMidi, viewMode, isPlaying, onViewModeChange, midiPlayer, handlePlayPause]);

  React.useEffect(() => {
    const audio = originalRef.current;
    if (!audio) return;
    const onEnded = () => setAudioPlaying(false);
    const onTimeUpdate = () => setAudioCurrentTime(audio.currentTime);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("timeupdate", onTimeUpdate);
    return () => {
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, []);

  const canPlay = (viewMode === "original" && originalSrc) || (viewMode === "midi" && midiSrc);

  return (
    <div className="audio-preview-bar">
      <audio ref={originalRef} src={originalSrc ?? undefined} onEnded={() => setAudioPlaying(false)} />

      <button
        className="audio-preview-play"
        onClick={handlePlayPause}
        disabled={!canPlay || midiPlayer.loading}
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {midiPlayer.loading ? (
          <span className="btn-spinner" aria-hidden="true" />
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            {isPlaying ? (
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            ) : (
              <path d="M8 5v14l11-7L8 5z" />
            )}
          </svg>
        )}
      </button>

      <div className="audio-preview-indicators">
        <button
          className={`audio-preview-indicator ${viewMode === "original" ? "active" : ""}`}
          onClick={() => hasMidi && onViewModeChange("original")}
          disabled={!originalSrc}
          aria-pressed={viewMode === "original"}
          title="Original audio"
        >
          <span className="audio-preview-indicator-dot" />
        </button>
        <button
          className={`audio-preview-indicator midi ${viewMode === "midi" ? "active" : ""}`}
          onClick={() => hasMidi && onViewModeChange("midi")}
          disabled={!hasMidi}
          aria-pressed={viewMode === "midi"}
          title="MIDI audio"
        >
          <span className="audio-preview-indicator-dot" />
        </button>
      </div>

      <div className="audio-preview-progress-wrap">
        <div className="audio-preview-progress">
          <div className="audio-preview-progress-fill" style={{ width: `${progressPercent}%` }} />
        </div>
        <div className="audio-preview-time">
          <span className="audio-preview-current">{displayCurrentTime}</span>
          <span className="audio-preview-sep">/</span>
          <span className="audio-preview-total">{displayDuration}</span>
        </div>
      </div>

      <div className="audio-preview-toggle-wrap">
        <button
          className={`audio-preview-toggle ${viewMode === "midi" ? "on" : ""}`}
          onClick={handleToggle}
          disabled={!hasMidi}
          aria-pressed={viewMode === "midi"}
          role="switch"
        >
          <span className="audio-preview-toggle-track">
            <span className="audio-preview-toggle-thumb" />
          </span>
        </button>
        <span className={`audio-preview-toggle-label ${viewMode === "midi" ? "active" : ""}`}>
          MIDI Audio
        </span>
      </div>
    </div>
  );
};

function parseDuration(s: string): number {
  const parts = s.split(":").map(Number);
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return 0;
}

export const App: React.FC = () => {
  const [status, setStatus] = useState<TranscriptionStatus>("idle");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [progressPercent, setProgressPercent] = useState<number | null>(null);
  const [progressStage, setProgressStage] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApiResult | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [viewMode, setViewMode] = useState<"original" | "midi">("original");
  // ByteDance transcription thresholds (expressed as percentages for UI, mapped to 0–1 on backend).
  // Defaults mirror the original model values: onset=0.30, offset=0.30, frame=0.10.
  const [onsetThresholdPct, setOnsetThresholdPct] = useState(30);
  const [offsetThresholdPct, setOffsetThresholdPct] = useState(30);
  const [frameThresholdPct, setFrameThresholdPct] = useState(10);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSheetViewModal, setShowSheetViewModal] = useState(false);
  const [backendAvailable, setBackendAvailable] = useState<boolean | null>(null);

  const isBusy = status === "uploading" || status === "transcribing";

  useEffect(() => {
    applySeo({
      title: "Fuwalo Star — AI Piano to MIDI & Sheet Music Converter",
      description:
        "Convert solo piano recordings into MIDI and sheet music with Fuwalo Star. Upload WAV or MP3, tune transcription sensitivity, and export files in minutes.",
      canonicalPath: "/star",
      ogType: "website",
    });
  }, []);

  useEffect(() => {
    checkBackendHealth().then(setBackendAvailable);

    // Re-check every 30s so the maintenance banner auto-dismisses when the server recovers
    const interval = setInterval(() => {
      checkBackendHealth().then(setBackendAvailable);
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  const onFileChange = useCallback((f: File | null) => {
    setFile(f);
    setResult(null);
    setError(null);
    setStatus(f ? "ready" : "idle");
  }, []);

  const onDrop: React.DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    if (isBusy) return;
    const droppedFile = event.dataTransfer.files?.[0];
    if (!droppedFile) return;
    if (!validateAudioFile(droppedFile)) {
      setError("Please drop a valid solo piano WAV or MP3 file under 30 minutes.");
      return;
    }
    onFileChange(droppedFile);
  };

  const onTranscribe = useCallback(async () => {
    if (!file || isBusy) return;
    setError(null);
    setStatus("uploading");
    try {
      if (!validateAudioFile(file)) {
        setStatus("ready");
        setError("Unsupported file type or file is too large.");
        return;
      }
      setStatus("transcribing");
      setStatusMessage("Connecting to transcription service…");
      setProgressPercent(5);
      setProgressStage("starting");
      const apiResult = await transcribeWithProgress(
        file,
        (message, meta) => {
          setStatusMessage(message);
          if (meta?.percent != null) setProgressPercent(meta.percent);
          if (meta?.stage) setProgressStage(meta.stage);
        },
        {
        onsetThreshold: onsetThresholdPct / 100,
        offsetThreshold: offsetThresholdPct / 100,
        frameThreshold: frameThresholdPct / 100,
        }
      );
      setResult(apiResult);
      setStatus("success");
      setStatusMessage(null);
      setProgressPercent(100);
      setProgressStage("done");

      const now = new Date();
      setHistory((prev) => [
        { id: `${now.getTime()}`, createdAt: now.toLocaleString(), title: apiResult.title, summary: apiResult.summary },
        ...prev
      ].slice(0, 10));
    } catch (err: unknown) {
      setStatusMessage(null);
      const message =
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again or use a different recording.";
      setError(message);
      setStatus("error");
      setProgressPercent(null);
      setProgressStage(null);
    }
  }, [file, isBusy, onsetThresholdPct, offsetThresholdPct, frameThresholdPct]);

  const [duration, setDuration] = useState<string>("0:00");
  const originalAudioUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  React.useEffect(() => () => { if (originalAudioUrl) URL.revokeObjectURL(originalAudioUrl); }, [originalAudioUrl]);

  React.useEffect(() => {
    if (!file) {
      setDuration("0:00");
      return;
    }
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      setDuration(formatDuration(audio.duration));
      URL.revokeObjectURL(audio.src);
    };
    audio.src = URL.createObjectURL(file);
    return () => URL.revokeObjectURL(audio.src);
  }, [file]);

  return (
    <div className="page">
      <header className="header">
        <div className="header-inner">
          <Link to="/" className="brand" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="brand-mark brand-mark--logo" aria-hidden="true">
              <img src={FUWALO_LOGO_PATH} alt="" width={32} height={32} />
            </div>
            <span className="brand-name">fuwalo star</span>
          </Link>
          <nav className="nav-links">
            <a href="#how-it-works" className="nav-link">How it works</a>
            <a href="#faq" className="nav-link">FAQ</a>
          </nav>
          <div className="header-actions">
            <button className="btn-upgrade" onClick={() => setShowUpgradeModal(true)}>Plans</button>
            <div className="avatar">FS</div>
          </div>
        </div>
      </header>

      <main className="dashboard">
        <section className="dashboard-left">
          <h1 className="dashboard-title">Piano to MIDI</h1>
          <p className="dashboard-subtitle">Transform your piano recordings into sheet music and MIDI with AI-powered transcription.</p>

          {backendAvailable === false && <MaintenanceBanner />}

          <div
            className="dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
          >
            <div className="dropzone-inner">
              <div className="dropzone-icon" aria-hidden="true">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
              </div>
              <p className="dropzone-text">Drop audio to convert to MIDI</p>
              <label className="btn-choose">
                <input
                  type="file"
                  accept=".wav,.mp3,audio/wav,audio/mpeg,audio/x-wav"
                  onChange={(e) => {
                    const next = e.target.files?.[0] ?? null;
                    if (next && !validateAudioFile(next)) {
                      setError("Please choose a WAV or MP3 file under 30 minutes.");
                      e.target.value = "";
                      return;
                    }
                    onFileChange(next);
                  }}
                />
                Choose files
              </label>
            </div>
          </div>

          {file && (
            <div className="file-card">
              <div className="file-card-info">
                <span className="file-card-name">{file.name}</span>
                <span className="file-card-duration">{duration}</span>
              </div>
              <button className="file-card-delete" onClick={() => onFileChange(null)} aria-label="Remove file">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14Z" />
                  <path d="M10 11v6M14 11v6" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          )}

          <button type="button" className="bulk-cta" onClick={() => setShowUpgradeModal(true)}>
            <div className="bulk-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6M12 18v-6M9 15h6" strokeLinecap="round" />
              </svg>
            </div>
            <span>Bulk convert coming soon &mdash; view upcoming plans</span>
          </button>

          <StatusLine
            status={status}
            error={error}
            statusMessage={statusMessage}
            backendAvailable={backendAvailable}
            progressPercent={progressPercent}
            progressStage={progressStage}
          />

          <button
            className="btn-primary"
            onClick={onTranscribe}
            disabled={!file || isBusy || backendAvailable === false}
          >
            {status === "transcribing" ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                Transcribing…
              </>
            ) : (
              "Convert to MIDI"
            )}
          </button>
        </section>

        <section className="dashboard-right">
          <div className="preview-card">
            <div className="preview-header">
              <span className="preview-title">{file ? file.name : "Preview"}</span>
              {file && <span className="preview-duration">{duration}</span>}
            </div>

            <div className="preview-area">
              {(file || result) ? (
                <div className="preview-content">
                  <div className="audio-preview-container">
                    <p className="audio-preview-label">Hear a sample</p>
                    <AudioPreviewBar
                      originalSrc={originalAudioUrl}
                      midiSrc={result?.midiUrl ?? null}
                      viewMode={viewMode}
                      onViewModeChange={setViewMode}
                      duration={duration}
                      hasMidi={!!result?.midiUrl}
                    />
                    {result && (
                      <div className="quick-actions">
                        {result.musicXmlUrl && (
                          <button
                            type="button"
                            className="btn-quick-action"
                            onClick={() => setShowSheetViewModal(true)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                            </svg>
                            View Sheet Music
                          </button>
                        )}
                        {result.midiUrl && (
                          <a href={result.midiUrl} download className="btn-quick-action btn-quick-download">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Download MIDI
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  {result?.plotUrl && (
                    <div className="preview-plot-wrap">
                      <img src={result.plotUrl} alt="MIDI score visualization" className="preview-plot" />
                    </div>
                  )}
                  {!result && file && (
                    <p className="preview-convert-hint">Convert to hear MIDI audio</p>
                  )}
                </div>
              ) : (
                <div className="preview-empty">
                  <div className="preview-empty-icon">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M9 18V5l12-2v13" />
                      <circle cx="6" cy="18" r="3" />
                      <circle cx="18" cy="16" r="3" />
                    </svg>
                  </div>
                  <p>Upload an audio file to preview</p>
                  <span className="preview-empty-hint">WAV or MP3, solo piano works best</span>
                </div>
              )}
            </div>

            {file && (
              <div className="controls-card">
                {result && (
                  <div className="toggle-group">
                    <button
                      className={`toggle-option ${viewMode === "original" ? "active" : ""}`}
                      onClick={() => setViewMode("original")}
                    >
                      <span className="toggle-dot" />
                      Original
                    </button>
                    <button
                      className={`toggle-option ${viewMode === "midi" ? "active" : ""}`}
                      onClick={() => setViewMode("midi")}
                    >
                      <span className="toggle-dot" />
                      MIDI
                    </button>
                  </div>
                )}

                <div className="slider-group">
                  <div className="slider-row">
                    <label className="slider-label">
                      Onset threshold
                      <span className="slider-info" title="Lower values detect more subtle note onsets">ⓘ</span>
                    </label>
                    <span className="slider-value">{onsetThresholdPct}%</span>
                  </div>
                  <div className="slider-track slider-track-gradient">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={onsetThresholdPct}
                      onChange={(e) => setOnsetThresholdPct(Number(e.target.value))}
                      className="slider-input"
                    />
                    <span className="slider-labels"><span>Less sensitive</span><span>More sensitive</span></span>
                  </div>
                </div>

                <div className="slider-group">
                  <div className="slider-row">
                    <label className="slider-label">
                      Offset threshold
                      <span className="slider-info" title="Controls how precisely note endings are detected">ⓘ</span>
                    </label>
                    <span className="slider-value">{offsetThresholdPct}%</span>
                  </div>
                  <div className="slider-track">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={offsetThresholdPct}
                      onChange={(e) => setOffsetThresholdPct(Number(e.target.value))}
                      className="slider-input"
                    />
                    <span className="slider-labels"><span>Softer tails</span><span>Tighter cutoffs</span></span>
                  </div>
                </div>

                <div className="slider-group">
                  <div className="slider-row">
                    <label className="slider-label">
                      Frame threshold
                      <span className="slider-info" title="Higher values reject more noise but may miss very soft notes">ⓘ</span>
                    </label>
                    <span className="slider-value">{frameThresholdPct}%</span>
                  </div>
                  <div className="slider-track">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={frameThresholdPct}
                      onChange={(e) => setFrameThresholdPct(Number(e.target.value))}
                      className="slider-input"
                    />
                    <span className="slider-labels"><span>More detail</span><span>Cleaner</span></span>
                  </div>
                </div>

                <p className="upgrade-hint">All features are free during early access</p>
                <button type="button" className="btn-trial" onClick={() => setShowUpgradeModal(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l2.4 7.4h7.6l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
                  </svg>
                  View upcoming plans
                </button>
              </div>
            )}

          </div>
        </section>
      </main>

      <section id="how-it-works" className="section section-light">
        <div className="shell">
          <h2 className="section-title">How it works</h2>
          <div className="cards-grid">
            <div className="info-card">
              <h3>1. Upload your recording</h3>
              <p>Drop a solo piano WAV or MP3. Clean recordings with minimal reverb produce the best results.</p>
            </div>
            <div className="info-card">
              <h3>2. AI transcription</h3>
              <p>Our AI analyzes your audio and generates precise note data with accurate timing and dynamics.</p>
            </div>
            <div className="info-card">
              <h3>3. Sheet music &amp; MIDI</h3>
              <p>Get beautiful sheet music, download MIDI for your DAW, and preview the rendered audio.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="section">
        <div className="shell">
          <h2 className="section-title">FAQ</h2>
          <div className="faq-grid">
            <div className="info-card">
              <h3>Is my audio stored?</h3>
              <p>We don&apos;t store your files. Processing runs on Hugging Face infrastructure with short-lived copies.</p>
            </div>
            <div className="info-card">
              <h3>What file formats?</h3>
              <p>WAV and MP3. Solo piano works best; full mixes or other instruments will degrade results.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="shell">
          <span className="footer-text">
            <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>fuwalo</Link> star &mdash; AI-powered piano transcription
          </span>
        </div>
      </footer>

      {showUpgradeModal && (
        <UpgradeModal onClose={() => setShowUpgradeModal(false)} />
      )}
      {showSheetViewModal && result && (
        <SheetViewModal
          musicXmlUrl={result.musicXmlUrl ?? null}
          midiUrl={result.midiUrl ?? null}
          originalAudioUrl={originalAudioUrl}
          duration={duration}
          title={result.title ? `Sheet view: ${result.title}` : "Sheet view"}
          onClose={() => setShowSheetViewModal(false)}
        />
      )}
    </div>
  );
};

const MAX_FILE_BYTES = 60 * 60 * 5 * 1024 * 16;

function validateAudioFile(file: File): boolean {
  const allowedTypes = ["audio/wav", "audio/x-wav", "audio/mpeg"];
  const allowedExtensions = [".wav", ".mp3"];
  const lowerName = file.name.toLowerCase();
  const hasAllowedExtension = allowedExtensions.some((ext) => lowerName.endsWith(ext));
  const hasAllowedType = allowedTypes.includes(file.type);
  const sizeOk = file.size > 0 && file.size <= MAX_FILE_BYTES;
  return sizeOk && (hasAllowedExtension || hasAllowedType);
}

/* -----------------------------------------------------------------------
 * Maintenance Banner — shown when the transcription backend is unreachable
 * ----------------------------------------------------------------------- */
const MaintenanceBanner: React.FC = () => (
  <div className="maintenance-banner" role="alert">
    <div className="maintenance-icon" aria-hidden="true">
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    </div>
    <div className="maintenance-content">
      <strong className="maintenance-title">Under Maintenance</strong>
      <p className="maintenance-text">
        Our transcription service is currently being updated. We&apos;ll be back shortly — thank you for your patience.
      </p>
    </div>
  </div>
);

type StatusLineProps = {
  status: TranscriptionStatus;
  error: string | null;
  statusMessage?: string | null;
  backendAvailable?: boolean | null;
  progressPercent?: number | null;
  progressStage?: string | null;
};

const StatusLine: React.FC<StatusLineProps> = ({
  status,
  error,
  statusMessage,
  backendAvailable,
  progressPercent,
  progressStage,
}) => {
  if (error) return <p className="status status-error">{error}</p>;
  if ((status === "uploading" || status === "transcribing") && statusMessage) {
    return (
      <div className="status status-busy status-progress">
        <div className="status-row">
          <span>{statusMessage}</span>
          {progressPercent != null && (
            <span className="status-percent">{Math.round(progressPercent)}%</span>
          )}
        </div>
        <div className="status-bar">
          <div
            className="status-bar-fill"
            style={{ width: `${Math.min(100, Math.max(0, progressPercent ?? 0))}%` }}
          />
        </div>
        {progressStage && <span className="status-stage">{progressStage}</span>}
      </div>
    );
  }
  if (backendAvailable === false && status === "idle") {
    return null; // Handled by the MaintenanceBanner in the parent component
  }
  switch (status) {
    case "idle":
      return <p className="status">Drop a solo piano recording to get started.</p>;
    case "ready":
      return <p className="status">Ready. Click Convert when you&apos;re set.</p>;
    case "success":
      return <p className="status status-success">Done. Download your MIDI below.</p>;
    default:
      return null;
  }
};
